/**
 * Ein Beleg mit seinen Positionen.
 *
 * Der Zustand entscheidet, was möglich ist — und zwar der Server, nicht
 * diese Seite. `aenderbar`, `herausgebbar` und `stornierbar` kommen mit
 * der Antwort; hier wird nur danach gefragt. Die Regeln nachzubauen hieße,
 * sie zweimal zu pflegen, und irgendwann liefen sie auseinander.
 *
 * Der wichtigste Übergang ist das Herausgeben: davor lässt sich alles
 * ändern, danach nichts mehr. Eine herausgegebene Rechnung trägt eine
 * Nummer aus einem lückenlosen Kreis und ist beim Kunden — sie zu ändern
 * hieße, ein Dokument zu verändern, das jemand anderes schon hat.
 */

import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  BELEGARTTEXT,
  BELEGSTATUSTEXT,
  euro,
  useBeleg,
  useBelegLoeschen,
  useBelegSchritt,
  usePositionAendern,
  usePositionAnlegen,
  usePositionLoeschen,
  type Position,
} from '../api/belege'
import {
  useAusKatalog,
  useBelegVersenden,
  useLeistungen,
  useVorlageEinsetzen,
  useVorlagen,
  type VersandErgebnis,
} from '../api/abrechnung'
import { Blatt } from '../bausteine/Blatt'
import { Fehlerhinweis, Ladeanzeige, datumDeutsch } from '../bausteine/Hinweise'

export function BelegDetail() {
  const { belegId = '' } = useParams()
  const gehZu = useNavigate()
  const beleg = useBeleg(belegId)
  const schritt = useBelegSchritt(belegId)
  const loeschen = useBelegLoeschen()
  const [stornogrund, setzeStornogrund] = useState('')
  const [versandOffen, setzeVersandOffen] = useState(false)
  const [stornoOffen, setzeStornoOffen] = useState(false)

  if (beleg.isLoading) return <Ladeanzeige />
  if (beleg.error) return <Fehlerhinweis fehler={beleg.error} />

  const b = beleg.data!
  const istRechnung = b.art === 'rechnung' || b.art === 'storno'

  return (
    <div className="space-y-4">
      <nav className="text-base">
        <Link to="/belege" className="text-marke-700 underline">
          ← Belege
        </Link>
      </nav>

      <header className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold">
            {b.nummer ?? 'Entwurf'} · {BELEGARTTEXT[b.art]}
          </h1>
          <p className="text-slate-700">
            <Link to={`/kunden/${b.kunde_id}`} className="text-marke-700 underline">
              {b.kunde_name}
            </Link>
            {b.betreff ? ` · ${b.betreff}` : ''} · {datumDeutsch(b.datum)}
          </p>
        </div>
        <span className="marke bg-marke-100 text-marke-700">
          {BELEGSTATUSTEXT[b.status]}
        </span>
      </header>

      <Fehlerhinweis fehler={schritt.error} />
      <Fehlerhinweis fehler={loeschen.error} />

      {b.aenderbar && (
        <p className="rounded-feld border-2 border-slate-200 bg-slate-50 p-3 text-base">
          Entwurf: alles lässt sich noch ändern. Mit dem Herausgeben bekommt der
          Beleg seine Nummer und ist danach unveränderlich.
        </p>
      )}

      <Positionen beleg={b} />

      {/* ── Summen ─────────────────────────────────────────────── */}
      <div className="karte">
        <dl className="ml-auto max-w-sm space-y-1">
          <div className="flex justify-between">
            <dt className="text-slate-700">Netto</dt>
            <dd className="tabular-nums">{euro(b.netto)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-700">
              Umsatzsteuer{b.kleinunternehmer ? ' (§ 19 UStG)' : ''}
            </dt>
            <dd className="tabular-nums">{euro(b.steuer)}</dd>
          </div>
          <div className="flex justify-between border-t-2 border-slate-200 pt-1 text-lg font-bold">
            <dt>Gesamt</dt>
            <dd className="tabular-nums">{euro(b.brutto)}</dd>
          </div>
        </dl>
      </div>

      {/* ── Schritte ───────────────────────────────────────────── */}
      <div className="karte space-y-3">
        <h2 className="text-lg font-bold">Was jetzt möglich ist</h2>

        <div className="flex flex-wrap gap-2">
          {b.herausgebbar && (
            <button
              type="button"
              className="knopf-haupt"
              disabled={schritt.isPending || b.positionen.length === 0}
              onClick={() => {
                if (
                  window.confirm(
                    'Beleg herausgeben?\n\nEr bekommt seine Nummer und ist danach ' +
                      'unveränderlich. Korrekturen laufen über eine Stornorechnung.',
                  )
                ) {
                  schritt.mutate({ schritt: 'herausgeben' })
                }
              }}
            >
              Herausgeben
            </button>
          )}

          {b.status !== 'entwurf' && (
            <a
              className="knopf-zweit"
              href={`/api/belege/${b.id}/pdf`}
              target="_blank"
              rel="noreferrer"
            >
              PDF
            </a>
          )}

          {istRechnung && b.status !== 'entwurf' && (
            <a
              className="knopf-zweit"
              href={`/api/belege/${b.id}/xrechnung.xml`}
            >
              XRechnung
            </a>
          )}

          {b.status !== 'entwurf' && (
            <button
              type="button"
              className="knopf-zweit"
              onClick={() => setzeVersandOffen(true)}
            >
              Per Mail schicken
            </button>
          )}

          {b.art === 'angebot' && b.status === 'herausgegeben' && (
            <button
              type="button"
              className="knopf-zweit"
              disabled={schritt.isPending}
              onClick={() =>
                schritt.mutate(
                  { schritt: 'uebernehmen', daten: { art: 'auftragsbestaetigung' } },
                  { onSuccess: (neu) => gehZu(`/belege/${neu.id}`) },
                )
              }
            >
              In Auftragsbestätigung übernehmen
            </button>
          )}

          {(b.art === 'angebot' || b.art === 'auftragsbestaetigung') &&
            b.status !== 'entwurf' && (
              <button
                type="button"
                className="knopf-zweit"
                disabled={schritt.isPending}
                onClick={() =>
                  schritt.mutate(
                    { schritt: 'uebernehmen', daten: { art: 'rechnung' } },
                    { onSuccess: (neu) => gehZu(`/belege/${neu.id}`) },
                  )
                }
              >
                In Rechnung übernehmen
              </button>
            )}

          {istRechnung && b.status === 'herausgegeben' && (
            <button
              type="button"
              className="knopf-zweit"
              disabled={schritt.isPending}
              onClick={() => schritt.mutate({ schritt: 'bezahlt' })}
            >
              Als bezahlt melden
            </button>
          )}

          {b.stornierbar && (
            <button
              type="button"
              className="knopf-warnung"
              onClick={() => setzeStornoOffen(true)}
            >
              Stornieren
            </button>
          )}

          {b.aenderbar && (
            <button
              type="button"
              className="knopf-warnung ml-auto"
              disabled={loeschen.isPending}
              onClick={() => {
                if (window.confirm('Diesen Entwurf löschen?')) {
                  loeschen.mutate(b.id, { onSuccess: () => gehZu('/belege') })
                }
              }}
            >
              Entwurf löschen
            </button>
          )}
        </div>

        {stornoOffen && (
          <div className="rounded-feld border-2 border-stufe-3 p-3">
            <p className="font-semibold text-stufe-3">Rechnung stornieren</p>
            <p className="mt-1 text-base">
              Es entsteht eine <strong>Stornorechnung</strong> mit negativen
              Beträgen und eigener Nummer. Die ursprüngliche Rechnung bleibt
              bestehen — so bleibt die Nummernfolge lückenlos und der Vorgang
              nachvollziehbar.
            </p>
            <div className="mt-2">
              <label htmlFor="storno-grund">Grund</label>
              <input
                id="storno-grund"
                value={stornogrund}
                onChange={(e) => setzeStornogrund(e.target.value)}
                maxLength={500}
                placeholder="Leistung nicht erbracht"
              />
            </div>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                className="knopf-warnung"
                disabled={schritt.isPending}
                onClick={() =>
                  schritt.mutate(
                    { schritt: 'stornieren', daten: { grund: stornogrund || null } },
                    {
                      onSuccess: (neu) => {
                        setzeStornoOffen(false)
                        gehZu(`/belege/${neu.id}`)
                      },
                    },
                  )
                }
              >
                Stornorechnung erzeugen
              </button>
              <button
                type="button"
                className="knopf-still"
                onClick={() => setzeStornoOffen(false)}
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {b.herausgegeben_am && (
          <p className="text-base text-slate-600">
            Herausgegeben am {datumDeutsch(b.herausgegeben_am)}
            {b.faellig_am ? ` · fällig ${datumDeutsch(b.faellig_am)}` : ''}
            {b.bezahlt_am ? ` · bezahlt ${datumDeutsch(b.bezahlt_am)}` : ''}
          </p>
        )}
      </div>

      {versandOffen && (
        <Versandblatt beleg={b} schliessen={() => setzeVersandOffen(false)} />
      )}
    </div>
  )
}

/**
 * Den Beleg per Mail hinausgeben.
 *
 * Die Adresse steht vorbelegt aus der Kundenakte und lässt sich für
 * diesen einen Versand überschreiben — das ist der Fall „schick es
 * ausnahmsweise an die Kämmerei". Geändert wird dabei nichts am Kunden;
 * wer die Adresse dauerhaft will, trägt sie dort ein.
 *
 * An einer Rechnung hängen zwei Dateien: das PDF und die XRechnung als
 * XML. Ob die zweite mitgeht, entscheidet der Server – dafür braucht es
 * die Leitweg-ID des Kunden, und die hat nur ein öffentlicher
 * Auftraggeber. Fehlt etwas, geht die Mail trotzdem, und hinterher steht
 * hier, was zu ergänzen wäre.
 */
function Versandblatt({
  beleg,
  schliessen,
}: {
  beleg: import('../api/belege').Beleg
  schliessen: () => void
}) {
  const istRechnung = beleg.art === 'rechnung' || beleg.art === 'storno'
  const versenden = useBelegVersenden(beleg.id)
  const [ergebnis, setzeErgebnis] = useState<VersandErgebnis | null>(null)

  return (
    <Blatt
      titel="Per Mail schicken"
      untertitel={`${BELEGARTTEXT[beleg.art]} ${beleg.nummer ?? ''} an ${beleg.kunde_name}`}
      schliessen={schliessen}
      fuss={
        ergebnis ? (
          <button type="button" className="knopf-haupt w-full" onClick={schliessen}>
            Fertig
          </button>
        ) : (
          <div className="flex gap-3">
            <button type="button" className="knopf-still flex-1" onClick={schliessen}>
              Abbrechen
            </button>
            <button
              type="submit"
              form="beleg-versand"
              className="knopf-haupt flex-1"
              disabled={versenden.isPending}
            >
              {versenden.isPending ? 'Schickt …' : 'Abschicken'}
            </button>
          </div>
        )
      }
    >
      {ergebnis ? (
        <div className="space-y-3">
          <div className="karte border-stufe-ob bg-stufe-ob-grund">
            <p className="font-semibold text-stufe-ob">{ergebnis.meldung}</p>
            <p className="mt-2 text-base text-slate-700">
              Der Versand steht jetzt auch in der Kundenakte. Dort lässt sich
              später nachlesen, wann der Beleg hinausging.
            </p>
          </div>

          {ergebnis.xrechnung_fehlt.length > 0 && (
            <div className="karte border-stufe-2 bg-stufe-2-grund">
              <p className="font-semibold text-stufe-2">
                Ohne XRechnung verschickt
              </p>
              <p className="mt-1 text-base text-slate-700">
                Öffentliche Auftraggeber nehmen vielfach nur noch die
                elektronische Rechnung an. Fehlt dieser Kunde in der Liste,
                weil er keine Leitweg-ID hat, ist alles in Ordnung — sonst
                bitte ergänzen und die Rechnung erneut schicken.
              </p>
              <ul className="mt-2 space-y-1 text-base">
                {ergebnis.xrechnung_fehlt.map((zeile) => (
                  <li key={zeile}>· {zeile}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <form
          id="beleg-versand"
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            const f = new FormData(e.currentTarget)
            const t = (n: string) => String(f.get(n) ?? '').trim()
            void versenden
              .mutateAsync({ an: t('an') || null, text: t('text') || null })
              .then(setzeErgebnis)
              .catch(() => {})
          }}
        >
          <div>
            <label htmlFor="bv-an">Empfänger</label>
            <input
              id="bv-an"
              name="an"
              type="email"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="Adresse aus der Kundenakte"
            />
            <p className="mt-1 text-base text-slate-600">
              Leer lassen heißt: an die Adresse, die beim Kunden steht.
            </p>
          </div>
          <div>
            <label htmlFor="bv-text">Begleittext</label>
            <textarea
              id="bv-text"
              name="text"
              rows={5}
              placeholder="Leer lassen heißt: der übliche kurze Text mit Nummer, Betrag und Zahlungsziel."
            />
          </div>
          <p className="rounded-feld border-2 border-marke-100 bg-marke-50 p-3 text-marke-700">
            {istRechnung
              ? 'Im Anhang liegen das PDF und – sofern die Angaben dafür vollständig sind – die XRechnung als XML.'
              : 'Im Anhang liegt der Beleg als PDF.'}
          </p>
          <Fehlerhinweis fehler={versenden.error} />
        </form>
      )}
    </Blatt>
  )
}

/**
 * Die Positionen.
 *
 * Am Entwurf änderbar, danach nur noch zu lesen — das entscheidet der
 * Server über `aenderbar`, nicht diese Liste.
 */
function Positionen({ beleg }: { beleg: import('../api/belege').Beleg }) {
  const anlegen = usePositionAnlegen(beleg.id)
  const aendern = usePositionAendern(beleg.id)
  const loeschen = usePositionLoeschen(beleg.id)
  const [bearbeitet, setzeBearbeitet] = useState<Position | null>(null)

  return (
    <section className="karte space-y-3">
      <h2 className="text-lg font-bold">Positionen</h2>

      {beleg.aenderbar && <AusDemKatalog beleg={beleg} />}

      <Fehlerhinweis fehler={anlegen.error} />
      <Fehlerhinweis fehler={aendern.error} />
      <Fehlerhinweis fehler={loeschen.error} />

      {beleg.positionen.length === 0 ? (
        <p className="text-slate-600">
          Noch keine Position. Ein Beleg ohne Positionen lässt sich nicht
          herausgeben.
        </p>
      ) : (
        <ul className="space-y-2">
          {beleg.positionen.map((p) => (
            <li
              key={p.id}
              className="flex flex-wrap items-start gap-3 border-t-2 border-slate-100 pt-2"
            >
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{p.bezeichnung}</div>
                {p.beschreibung && (
                  <div className="text-base text-slate-600">{p.beschreibung}</div>
                )}
                <div className="text-base text-slate-700 tabular-nums">
                  {Number(p.menge).toLocaleString('de-DE')} {p.einheit} ×{' '}
                  {euro(p.einzelpreis)} · {Number(p.steuersatz).toLocaleString('de-DE')} %
                </div>
              </div>
              <div className="shrink-0 text-right font-semibold tabular-nums">
                {euro(p.summe)}
              </div>
              {beleg.aenderbar && (
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    className="knopf-still px-2 text-base"
                    onClick={() => setzeBearbeitet(p)}
                  >
                    Ändern
                  </button>
                  <button
                    type="button"
                    className="knopf-still px-2 text-base"
                    disabled={loeschen.isPending}
                    onClick={() => loeschen.mutate(p.id)}
                  >
                    Entfernen
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {beleg.aenderbar && (
        <form
          className="grid gap-2 border-t-2 border-slate-200 pt-3 sm:grid-cols-[2fr_5rem_6rem_7rem_auto]"
          onSubmit={(e) => {
            e.preventDefault()
            const formular = e.currentTarget
            const f = new FormData(formular)
            const t = (n: string) => String(f.get(n) ?? '').trim()
            anlegen.mutate(
              {
                bezeichnung: t('bezeichnung'),
                menge: t('menge') || '1',
                einheit: t('einheit') || 'Stück',
                einzelpreis: t('einzelpreis'),
                steuersatz: t('steuersatz') || '19',
              },
              { onSuccess: () => formular.reset() },
            )
          }}
        >
          <div className="sm:col-span-5">
            <label htmlFor="p-bez">Neue Position</label>
          </div>
          <input
            id="p-bez"
            name="bezeichnung"
            required
            maxLength={200}
            placeholder="Hauptuntersuchung Spielplatz …"
          />
          <input name="menge" type="number" step="0.01" defaultValue="1" aria-label="Menge" />
          <input name="einheit" defaultValue="Stück" aria-label="Einheit" />
          <input
            name="einzelpreis"
            type="number"
            step="0.01"
            required
            placeholder="0,00"
            aria-label="Einzelpreis"
          />
          <button type="submit" className="knopf-zweit px-3" disabled={anlegen.isPending}>
            {anlegen.isPending ? '…' : 'Hinzufügen'}
          </button>
        </form>
      )}

      {bearbeitet && (
        <PositionBlatt
          position={bearbeitet}
          speichern={(daten) =>
            aendern.mutate(
              { id: bearbeitet.id, ...daten },
              { onSuccess: () => setzeBearbeitet(null) },
            )
          }
          laeuft={aendern.isPending}
          schliessen={() => setzeBearbeitet(null)}
        />
      )}
    </section>
  )
}

function PositionBlatt({
  position,
  speichern,
  laeuft,
  schliessen,
}: {
  position: Position
  speichern: (daten: Record<string, unknown>) => void
  laeuft: boolean
  schliessen: () => void
}) {
  return (
    <div className="rounded-feld border-2 border-akzent-500 p-3">
      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault()
          const f = new FormData(e.currentTarget)
          const t = (n: string) => String(f.get(n) ?? '').trim()
          speichern({
            bezeichnung: t('bezeichnung'),
            beschreibung: t('beschreibung') || null,
            menge: t('menge'),
            einheit: t('einheit'),
            einzelpreis: t('einzelpreis'),
            steuersatz: t('steuersatz'),
          })
        }}
      >
        <div>
          <label htmlFor="pb-bez">Bezeichnung</label>
          <input
            id="pb-bez"
            name="bezeichnung"
            required
            maxLength={200}
            defaultValue={position.bezeichnung}
          />
        </div>
        <div>
          <label htmlFor="pb-beschr">Beschreibung</label>
          <textarea
            id="pb-beschr"
            name="beschreibung"
            rows={2}
            defaultValue={position.beschreibung ?? ''}
          />
        </div>
        <div className="grid gap-2 sm:grid-cols-4">
          <div>
            <label htmlFor="pb-menge">Menge</label>
            <input
              id="pb-menge"
              name="menge"
              type="number"
              step="0.01"
              defaultValue={position.menge}
            />
          </div>
          <div>
            <label htmlFor="pb-einheit">Einheit</label>
            <input id="pb-einheit" name="einheit" defaultValue={position.einheit} />
          </div>
          <div>
            <label htmlFor="pb-preis">Einzelpreis</label>
            <input
              id="pb-preis"
              name="einzelpreis"
              type="number"
              step="0.01"
              defaultValue={position.einzelpreis}
            />
          </div>
          <div>
            <label htmlFor="pb-satz">USt. %</label>
            <input
              id="pb-satz"
              name="steuersatz"
              type="number"
              step="0.01"
              defaultValue={position.steuersatz}
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button type="submit" className="knopf-haupt" disabled={laeuft}>
            {laeuft ? 'Speichert …' : 'Übernehmen'}
          </button>
          <button type="button" className="knopf-still" onClick={schliessen}>
            Abbrechen
          </button>
        </div>
      </form>
    </div>
  )
}


/**
 * Zeilen aus dem Katalog statt aus dem Kopf.
 *
 * Zwei Wege nebeneinander, weil es zwei Fälle sind: eine einzelne
 * Leistung ist der Nachschlag („und noch die Anfahrt"), eine Vorlage ist
 * der Anfang („das jährliche Angebot an eine Gemeinde").
 *
 * Was hier hineinkommt, wird **abgeschrieben**. Ändert sich der
 * Katalogpreis morgen, bleibt diese Zeile, wie sie ist — ein Beleg zeigt,
 * was zum Zeitpunkt des Schreibens galt.
 */
function AusDemKatalog({ beleg }: { beleg: import('../api/belege').Beleg }) {
  const leistungen = useLeistungen()
  const vorlagen = useVorlagen()
  const ausKatalog = useAusKatalog(beleg.id)
  const einsetzen = useVorlageEinsetzen(beleg.id)

  const katalog = leistungen.data ?? []
  const muster = vorlagen.data ?? []

  // Nichts gepflegt: dann steht hier auch nichts. Zwei leere Auswahllisten
  // wären nur Rauschen über dem Formular, das ohnehin funktioniert.
  if (katalog.length === 0 && muster.length === 0) return null

  return (
    <div className="rounded-feld border-2 border-slate-200 bg-slate-50 p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {katalog.length > 0 && (
          <div>
            <label htmlFor="bd-katalog">Leistung aus dem Katalog</label>
            <select
              id="bd-katalog"
              value=""
              disabled={ausKatalog.isPending}
              onChange={(e) => {
                if (!e.target.value) return
                ausKatalog.mutate({ leistung_id: e.target.value })
                e.target.value = ''
              }}
            >
              <option value="">Hinzufügen …</option>
              {katalog.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.bezeichnung} — {euro(l.einzelpreis)}
                </option>
              ))}
            </select>
          </div>
        )}

        {muster.length > 0 && (
          <div>
            <label htmlFor="bd-vorlage">Ganze Vorlage</label>
            <select
              id="bd-vorlage"
              value=""
              disabled={einsetzen.isPending}
              onChange={(e) => {
                if (!e.target.value) return
                einsetzen.mutate({ vorlage_id: e.target.value })
                e.target.value = ''
              }}
            >
              <option value="">Einsetzen …</option>
              {muster.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} — {euro(v.netto)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-base text-slate-600">
              Positionen werden angehängt; Texte nur gesetzt, wo noch nichts
              steht.
            </p>
          </div>
        )}
      </div>

      <Fehlerhinweis fehler={ausKatalog.error ?? einsetzen.error} />
    </div>
  )
}
